'use client';

import { Search, Loader2 } from 'lucide-react';
import { TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type {
  SearchableItem,
  RegistrationLiveItem
} from '@/hooks/use-qr-live-search';

interface NavigateResult {
  id: number;
  display: string;
}

interface QrManualTabProps {
  manualInput: string;
  onManualInputChange: (value: string) => void;
  onSearch: () => void;
  isFetching: boolean;
  searchTriggered: boolean;
  results: NavigateResult[];
  liveResults: SearchableItem[] | RegistrationLiveItem[];
  isLiveSearching: boolean;
  searchableItems?: SearchableItem[];
  isOnScanValue: boolean;
  stage: string;
  onNavigate: (id: number) => void;
  onSelectLiveResult: (value: string) => void;
  t: (key: string) => string;
}

export function QrManualTabContent({
  manualInput,
  onManualInputChange,
  onSearch,
  isFetching,
  searchTriggered,
  results,
  liveResults,
  isLiveSearching,
  searchableItems,
  isOnScanValue,
  stage,
  onNavigate,
  onSelectLiveResult,
  t
}: QrManualTabProps) {
  return (
    <TabsContent value='manual' className='mt-4 space-y-4'>
      {isOnScanValue ? (
        <>
          <div className='flex gap-2'>
            <div className='relative flex-1'>
              <Search className='text-muted-foreground absolute inset-s-3 top-1/2 h-4 w-4 -translate-y-1/2' />
              <Input
                placeholder={t('enterContainerNumber')}
                value={manualInput}
                onChange={(e) => onManualInputChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onSearch();
                }}
                className='ps-9'
              />
            </div>
            <Button onClick={onSearch} disabled={!manualInput.trim()}>
              <Search className='h-4 w-4' />
            </Button>
          </div>

          {!searchableItems && manualInput.length < 3 && (
            <p className='text-muted-foreground text-center text-sm'>
              {t('typeToSearch')}
            </p>
          )}

          {searchableItems && manualInput.length < 1 && (
            <p className='text-muted-foreground text-center text-sm'>
              {t('typeToSearch')}
            </p>
          )}

          {!searchableItems && manualInput.length >= 3 && isLiveSearching && (
            <div className='flex justify-center py-4'>
              <Loader2 className='text-muted-foreground h-5 w-5 animate-spin' />
            </div>
          )}

          {((searchableItems && manualInput.length >= 1) ||
            (!searchableItems && manualInput.length >= 3)) &&
            !isLiveSearching &&
            liveResults.length === 0 && (
              <p className='text-muted-foreground text-center text-sm'>
                {t('noMatch')}
              </p>
            )}

          {liveResults.length > 0 && (
            <div className='max-h-75 space-y-2 overflow-y-auto'>
              {searchableItems || stage === 'photo-scanning'
                ? (liveResults as SearchableItem[]).map((item) => (
                    <button
                      key={item.value}
                      type='button'
                      className='hover:bg-muted w-full rounded-lg border p-3 text-start transition-colors'
                      onClick={() => onSelectLiveResult(item.value)}
                    >
                      <p className='font-mono font-semibold'>{item.label}</p>
                      {item.sublabel && (
                        <p className='text-muted-foreground text-sm'>
                          {item.sublabel}
                        </p>
                      )}
                    </button>
                  ))
                : (liveResults as RegistrationLiveItem[]).map((item) => (
                    <button
                      key={item.id}
                      type='button'
                      className='hover:bg-muted w-full rounded-lg border p-3 text-start transition-colors'
                      onClick={() => onSelectLiveResult(item.container_number)}
                    >
                      <p className='font-mono font-semibold'>
                        {item.container_number}
                      </p>
                      {(item.container_type?.name || item.entity_name_ar) && (
                        <p className='text-muted-foreground text-sm'>
                          {item.container_type?.name ?? item.entity_name_ar}
                        </p>
                      )}
                    </button>
                  ))}
            </div>
          )}
        </>
      ) : (
        <>
          <div className='flex gap-2'>
            <Input
              placeholder={t('enterContainerNumber')}
              value={manualInput}
              onChange={(e) => {
                onManualInputChange(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSearch();
              }}
            />
            <Button
              onClick={onSearch}
              disabled={!manualInput.trim() || isFetching}
            >
              <Search className='h-4 w-4' />
            </Button>
          </div>

          {searchTriggered && !isFetching && results.length === 0 && (
            <p className='text-muted-foreground text-center text-sm'>
              {t('noMatch')}
            </p>
          )}

          {results.length > 0 && (
            <div className='space-y-2'>
              {results.map((item) => (
                <button
                  key={item.id}
                  type='button'
                  className='hover:bg-muted w-full rounded-lg border p-3 text-start transition-colors'
                  onClick={() => onNavigate(item.id)}
                >
                  <p className='font-medium'>{item.display}</p>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </TabsContent>
  );
}
